import { ProductAttrFilterMode } from "./ProductAttrFilterMode.mjs";
export class ProductAttrConditionEvaluator {
    constructor() {
        this.debugEnabled = false;
        this.debugList = [];
        this.attributes = {};
        this.ignoredConstraints = {};
        this.debugConstraints = {};
        this.regex = /[$][{].*?[}]/g;
        this.logEnabled = false;
    }
    reset(attributes) {
        this.attributes = attributes;
    }
    clearDebug() {
        this.debugList = [];
        this.debugConstraints = {};
    }
    debug(result) {
        this.debugList.push(result);
    }
    debugConstraint(attributeName, attributeOption, reasonOfConstraint) {
        if (this.debugEnabled) {
            if (!this.debugConstraints.hasOwnProperty(attributeName)) {
                this.debugConstraints[attributeName] = {};
            }
            if (!this.debugConstraints[attributeName].hasOwnProperty(attributeOption)) {
                this.debugConstraints[attributeName][attributeOption] = [];
            }
            this.debugConstraints[attributeName][attributeOption].push(reasonOfConstraint);
        }
    }
    getDebugConstraints(attributeName, attributeOption) {
        if (this.debugConstraints.hasOwnProperty(attributeName)) {
            if (this.debugConstraints[attributeName].hasOwnProperty(attributeOption)) {
                return this.debugConstraints[attributeName][attributeOption];
            }
        }
        return null;
    }
    log(...args) {
        if (this.logEnabled) {
            console.log(...args);
        }
    }
    ignoreConstraintsFromTo(fromAttribute, toAttribute) {
        if (!this.ignoredConstraints.hasOwnProperty(fromAttribute)) {
            this.ignoredConstraints[fromAttribute] = [];
        }
        this.ignoredConstraints[fromAttribute].push(toAttribute);
    }
    shouldIgnoreConstraints(fromAttribute, toAttribute) {
        if (this.ignoredConstraints.hasOwnProperty(fromAttribute) || this.ignoredConstraints.hasOwnProperty('*')) {
            return this.ignoredConstraints[fromAttribute].indexOf(toAttribute) >= 0 || this.ignoredConstraints[fromAttribute].indexOf('*') >= 0;
        }
        return false;
    }
    filter(productItemAsOneDimensionalObject, attrName) {
        const attr = this.getAttribute(attrName);
        let values = [];
        let filters = [];
        if (attr === null || attr === void 0 ? void 0 : attr.filters) {
            // 1. All filters that passes conditions
            filters = attr.filters.filter((attrFilter) => this.evalCondition(attrFilter.conditions, productItemAsOneDimensionalObject));
            // 2. Sort them by complexity score, highest complexity score wins
            filters.sort((a, b) => b.conditionsComplexityScore - a.conditionsComplexityScore);
            if (filters.length) {
                switch (attr.filterMode) {
                    case ProductAttrFilterMode.MODE_MERGE_ALL_WINNERS:
                        filters.forEach((filter) => (values = values.concat(filter.values)));
                        values = [...new Set(values)];
                        break;
                    default:
                        values = filters[0].values;
                        break;
                }
                if (!attr.isDynamicValue) {
                    // Removing invalid values
                    values = values.filter((value) => attr.valuesAndConstraints.hasOwnProperty(value.toString()));
                }
            }
        }
        return [values, filters];
    }
    /**
     * Will test against constraints to see if a change is possible.
     *
     * @param productItemAsOneDimensionalObject
     * @param attrNameToBeChanged
     * @param attrValueToBeChanged
     */
    canIChangeAttribute(productItemAsOneDimensionalObject, attrNameToBeChanged, attrValueToBeChanged) {
        // First we test if any of the current item values has any objections on changing this attr value
        if (!this.doesCurrentItemAllowToChange(productItemAsOneDimensionalObject, attrNameToBeChanged, attrValueToBeChanged)) {
            return false;
        }
        // If not we test the other way around: if the new value has any objections on the current attribute values
        return this.doesAttrValueAllowToBeCombinedWith(productItemAsOneDimensionalObject, attrNameToBeChanged, attrValueToBeChanged);
    }
    doesAttrValueAllowToBeCombinedWith(productItemAsOneDimensionalObject, attrNameToBeChanged, attrValueToBeChanged) {
        const condition = this.getConstraintsFor(attrNameToBeChanged, attrValueToBeChanged);
        if (condition) {
            let result = true;
            const conditionedColumnNames = condition.match(this.regex);
            if (conditionedColumnNames) {
                conditionedColumnNames.forEach((conditionColumnNameAsCode) => {
                    const conditionedColumnName = this.extractConditionedColumnName(conditionColumnNameAsCode);
                    if (conditionedColumnName && result) {
                        result = this.evalConditionAgainstChangeRequest(condition, productItemAsOneDimensionalObject, conditionedColumnName, productItemAsOneDimensionalObject[conditionedColumnName]);
                    }
                });
                if (!result && this.debugEnabled) {
                    let debugMsg = 'Blocked by ';
                    let debugValues = [];
                    conditionedColumnNames.forEach((conditionColumnNameAsCode) => {
                        const conditionedColumnName = this.extractConditionedColumnName(conditionColumnNameAsCode);
                        if (conditionedColumnName) {
                            const conditionedColumnNiceName = conditionedColumnName.replace('item.attributes.', '');
                            debugValues.push(`'${conditionedColumnNiceName}' (${productItemAsOneDimensionalObject[conditionedColumnName]})`);
                        }
                    });
                    debugValues = [...new Set(debugValues)];
                    if (debugValues.length > 1) {
                        debugMsg += 'any of these: ';
                    }
                    debugMsg += debugValues.join(', ');
                    this.debugConstraint(attrNameToBeChanged, attrValueToBeChanged, debugMsg);
                    this.debug(debugMsg);
                }
            }
            return result;
        }
        return true;
    }
    doesCurrentItemAllowToChange(productItemAsOneDimensionalObject, attrNameToBeChanged, attrValueToBeChanged) {
        for (const columnName in productItemAsOneDimensionalObject) {
            const attrName = columnName.indexOf('item.attributes.') >= 0 ? columnName.replace('item.attributes.', '') : false;
            if (attrName && attrName != attrNameToBeChanged) {
                if (!this.shouldIgnoreConstraints(attrName, attrNameToBeChanged)) {
                    const attr = this.getAttribute(attrName);
                    if (attr) {
                        let allValues = productItemAsOneDimensionalObject[columnName];
                        if (!Array.isArray(allValues)) {
                            allValues = [allValues];
                        }
                        for (const attrValueIndex in allValues) {
                            const attrValue = allValues[attrValueIndex];
                            const condition = this.getConstraintsFor(attrName, `${attrValue}`);
                            if (condition) {
                                const result = this.evalConditionAgainstChangeRequest(condition, productItemAsOneDimensionalObject, 'item.attributes.' + attrNameToBeChanged, attrValueToBeChanged);
                                if (!result) {
                                    const debug = `'${attrValue} ${attrName}' doesn't allow '${attrNameToBeChanged}' to be changed to '${attrValueToBeChanged}'.`;
                                    this.debugConstraint(attrNameToBeChanged, attrValueToBeChanged, `Blocked by '${attrName}' (${attrValue})`);
                                    this.log(debug, attr, condition);
                                    this.debug(debug);
                                    return result;
                                }
                                else {
                                    this.log(`'${attr}:${attrValue}'`, true);
                                }
                            }
                        }
                    }
                }
            }
        }
        return true;
    }
    evalConditionAgainstChangeRequest(condition, productItemAsOneDimensionalObject, columnNameToBeChanged, columnValueToBeChanged) {
        let result = true;
        // Does this condition care about the attr that we're testing?
        if (condition.indexOf('${' + columnNameToBeChanged + '}') >= 0) {
            const replacedValues = {};
            const conditionedColumnNames = condition.match(this.regex);
            if (conditionedColumnNames) {
                conditionedColumnNames.forEach((conditionColumnNameAsCode) => {
                    const columnName = this.extractConditionedColumnName(conditionColumnNameAsCode);
                    if (columnName == columnNameToBeChanged) {
                        condition = condition.replace(conditionColumnNameAsCode, 'columnValueToBeChanged');
                        replacedValues['columnValueToBeChanged'] = columnValueToBeChanged;
                    }
                    else {
                        condition = condition.replace(conditionColumnNameAsCode, "productItemAsOneDimensionalObject['" + columnName + "']");
                        replacedValues['productItemAsOneDimensionalObject[' + columnName + ']'] = productItemAsOneDimensionalObject[columnName];
                    }
                });
            }
            try {
                this.log('testConditionAgainstChangeRequest:', condition, replacedValues);
                result = eval(condition);
            }
            catch (e) { }
        }
        return result;
    }
    evalCondition(condition, productItemAsOneDimensionalObject) {
        let result = true;
        if (condition && condition.length) {
            // Does this condition care about the attr that we're testing?
            const replacedValues = {};
            const conditionedColumnNames = condition.match(this.regex);
            if (conditionedColumnNames) {
                conditionedColumnNames.forEach((conditionColumnNameAsCode) => {
                    const columnName = this.extractConditionedColumnName(conditionColumnNameAsCode);
                    condition = condition.replace(conditionColumnNameAsCode, "productItemAsOneDimensionalObject['" + columnName + "']");
                    replacedValues['productItemAsOneDimensionalObject[' + columnName + ']'] = productItemAsOneDimensionalObject[columnName];
                });
            }
            try {
                this.log('simpleConditionTest:', condition, replacedValues);
                result = eval(condition);
            }
            catch (e) { }
        }
        return result;
    }
    isNotIn(columnValue, conditionValue) {
        if (!columnValue) {
            return true;
        }
        if (Array.isArray(columnValue) && !Array.isArray(conditionValue)) {
            return columnValue.indexOf(conditionValue) < 0;
        }
        else if (!Array.isArray(columnValue) && Array.isArray(conditionValue)) {
            return conditionValue.indexOf(columnValue) < 0;
        }
        else if (Array.isArray(columnValue) && Array.isArray(conditionValue)) {
            return conditionValue.filter((value) => columnValue.indexOf(value) >= 0).length == 0;
        }
        else if (!Array.isArray(columnValue) && !Array.isArray(conditionValue)) {
            return columnValue != conditionValue;
        }
        return false;
    }
    isIn(columnValue, conditionValue) {
        if (!columnValue) {
            return false;
        }
        if (Array.isArray(columnValue) && !Array.isArray(conditionValue)) {
            return columnValue.indexOf(conditionValue) >= 0;
        }
        else if (!Array.isArray(columnValue) && Array.isArray(conditionValue)) {
            return conditionValue.indexOf(columnValue) >= 0;
        }
        else if (Array.isArray(columnValue) && Array.isArray(conditionValue)) {
            return conditionValue.filter((value) => columnValue.indexOf(value) >= 0).length > 0;
        }
        else if (!Array.isArray(columnValue) && !Array.isArray(conditionValue)) {
            return columnValue == conditionValue;
        }
        return false;
    }
    extractConditionedColumnName(condition) {
        var _a;
        const conditionedKey = (_a = condition.match(this.regex)) === null || _a === void 0 ? void 0 : _a.pop();
        if (conditionedKey) {
            return conditionedKey.replace('$', '').replace('{', '').replace('}', '');
        }
        return null;
    }
    /* SET AND GET */
    getConstraintsFor(attributeName, attributeValue) {
        const attr = this.getAttribute(attributeName);
        if (attr) {
            if (attr.valuesAndConstraints.hasOwnProperty(attributeValue)) {
                return attr.valuesAndConstraints[attributeValue];
            }
        }
        return null;
    }
    getAttribute(attributeName) {
        if (this.attributes.hasOwnProperty(attributeName)) {
            return this.attributes[attributeName];
        }
        return null;
    }
}
